<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  templates: { type: Object, default: null },
  selectedIndustryId: { type: String, default: '' },
});

const UI_LABELS = {
  title: 'Fragemallar',
  empty:
    'Valj en verksamhetstyp for att se vilka fragor Chatlotta kan forbereda.',
  global: 'Alla verksamheter',
  custom:
    'Egen verksamhetstyp anvander globala fragor och kan kompletteras med egna fragor senare.',
  required: 'Kravs',
  optional: 'Frivillig',
  booking: 'Bokning',
  botAllowed: 'Bot kan anvanda',
};

const FIELD_TYPE_LABELS = {
  short_text: 'Kort text',
  long_text: 'Lang text',
  phone: 'Telefon',
  email: 'E-post',
  url: 'Lank',
  address: 'Adress',
  opening_hours: 'Oppettider',
  price: 'Pris',
  yes_no: 'Ja/nej',
  multi_select: 'Flerval',
  single_select: 'Val',
  list: 'Lista',
  service_duration: 'Tidsatgang',
  booking_rules: 'Bokningsregler',
  custom_faq: 'Egen FAQ',
};

const isCustomOrOther = computed(() =>
  ['other', 'custom'].some(prefix =>
    props.selectedIndustryId.startsWith(prefix)
  )
);

const selectedIndustry = computed(() => {
  if (!props.templates?.industries || isCustomOrOther.value) return null;
  return props.templates.industries[props.selectedIndustryId] || null;
});

const globalQuestions = computed(() => props.templates?.global_questions || []);
const industryQuestions = computed(
  () => selectedIndustry.value?.questions || []
);
const hasPreview = computed(
  () =>
    Boolean(props.templates) &&
    (Boolean(selectedIndustry.value) || isCustomOrOther.value)
);

const fieldTypeLabel = fieldType => FIELD_TYPE_LABELS[fieldType] || fieldType;
</script>

<template>
  <div class="border-t border-n-weak px-3 py-3">
    <div class="mb-3 flex items-center gap-2">
      <Icon icon="i-lucide-list-checks" class="size-4 text-n-slate-9" />
      <span class="text-sm font-medium text-n-slate-12">
        {{ UI_LABELS.title }}
      </span>
    </div>

    <p v-if="!hasPreview" class="mb-0 text-sm text-n-slate-11">
      {{ UI_LABELS.empty }}
    </p>

    <div v-else class="space-y-4">
      <p v-if="isCustomOrOther" class="mb-0 text-sm text-n-slate-11">
        {{ UI_LABELS.custom }}
      </p>

      <section v-if="globalQuestions.length" class="space-y-2">
        <h3 class="mb-0 text-xs font-medium uppercase text-n-slate-10">
          {{ UI_LABELS.global }}
        </h3>
        <div class="grid gap-2 sm:grid-cols-2">
          <article
            v-for="question in globalQuestions"
            :key="`global-${question.id}`"
            class="rounded-md border border-n-weak px-3 py-2"
          >
            <div class="text-sm font-medium text-n-slate-12">
              {{ question.label_sv }}
            </div>
            <div class="mt-2 flex flex-wrap gap-1">
              <span
                class="rounded bg-n-alpha-2 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{ fieldTypeLabel(question.field_type) }}
              </span>
              <span
                class="rounded bg-n-alpha-2 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{
                  question.required ? UI_LABELS.required : UI_LABELS.optional
                }}
              </span>
              <span
                v-if="question.affects_booking"
                class="rounded bg-n-amber-3 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{ UI_LABELS.booking }}
              </span>
              <span
                v-if="question.bot_allowed"
                class="rounded bg-n-teal-3 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{ UI_LABELS.botAllowed }}
              </span>
            </div>
          </article>
        </div>
      </section>

      <section v-if="industryQuestions.length" class="space-y-2">
        <h3 class="mb-0 text-xs font-medium uppercase text-n-slate-10">
          {{ selectedIndustry.label_sv }}
        </h3>
        <div class="grid gap-2 sm:grid-cols-2">
          <article
            v-for="question in industryQuestions"
            :key="`industry-${question.id}`"
            class="rounded-md border border-n-weak px-3 py-2"
          >
            <div class="text-sm font-medium text-n-slate-12">
              {{ question.label_sv }}
            </div>
            <div class="mt-2 flex flex-wrap gap-1">
              <span
                class="rounded bg-n-alpha-2 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{ fieldTypeLabel(question.field_type) }}
              </span>
              <span
                class="rounded bg-n-alpha-2 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{
                  question.required ? UI_LABELS.required : UI_LABELS.optional
                }}
              </span>
              <span
                v-if="question.affects_booking"
                class="rounded bg-n-amber-3 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{ UI_LABELS.booking }}
              </span>
              <span
                v-if="question.bot_allowed"
                class="rounded bg-n-teal-3 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{ UI_LABELS.botAllowed }}
              </span>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>
